import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import { formatOrderReference } from '../../../../../utils/formatOrderReference';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const WORKSPACE_BASE = '/workspace';

type DetailResponse = {
  archive: {
    id: string;
    emailNormalized: string;
    formerUserId: string;
    trigger: string;
    deletedAt: string;
    payload: Record<string, unknown>;
  };
  retainedEmail: {
    deleteCount: number;
    firstSeenAt: string;
    lastDeletedAt: string | null;
  } | null;
  liveOrders: Array<{
    id: string;
    status: string;
    createdAt: string;
    totalUsd: unknown;
    customerId: string | null;
    deliveryBatchId: string | null;
    deliveryBatch: {
      id: string;
      areaLabel: string;
      windowStart: string;
      windowEnd: string;
      status: string;
    } | null;
  }>;
};

export default function CustomerArchiveDetailPage() {
  const { archiveId } = useParams<{ archiveId: string }>();
  const { authorizedFetch, auth } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canOrderRead = (auth.permissions || []).includes('ORDER:READ');

  useEffect(() => {
    if (!archiveId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authorizedFetch(
          `${API_BASE}/auth/workspace/customers/deletion-archives/${archiveId}`,
        );
        if (res.status === 404) {
          throw new Error(t('backoffice.customer_archive_detail_not_found'));
        }
        if (!res.ok) {
          throw new Error(t('backoffice.customer_archive_detail_error'));
        }
        const json = (await res.json()) as DetailResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : t('backoffice.customer_archive_detail_error'),
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [archiveId, authorizedFetch, t]);

  const payloadJson = data
    ? JSON.stringify(data.archive.payload, null, 2)
    : '';

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={`${WORKSPACE_BASE}/customers/deletion-archives`}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            ← {t('backoffice.customer_archive_detail_back')}
          </Link>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
            {t('backoffice.customer_archive_detail_title')}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('backoffice.customer_archive_detail_subtitle')}
          </p>
        </div>

        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : loading ? (
          <PaginatedListLoading skeletonRows={6} />
        ) : !data ? null : (
          <div className="space-y-8">
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <h2 className="text-base font-semibold text-slate-900">
                {t('backoffice.customer_archive_detail_summary')}
              </h2>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500">
                    {t('backoffice.customer_archives_col_deleted')}
                  </dt>
                  <dd className="font-medium text-slate-900">
                    {new Date(data.archive.deletedAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">
                    {t('backoffice.customer_archive_detail_trigger')}
                  </dt>
                  <dd className="font-medium text-slate-900">
                    {data.archive.trigger}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500">
                    {t('backoffice.customer_archives_col_email')}
                  </dt>
                  <dd className="break-all font-mono text-xs text-slate-800">
                    {data.archive.emailNormalized}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500">
                    {t('backoffice.customer_archives_col_former_id')}
                  </dt>
                  <dd className="break-all font-mono text-xs text-slate-700">
                    {data.archive.formerUserId}
                  </dd>
                </div>
              </dl>
            </section>

            {data.retainedEmail ? (
              <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm sm:p-6">
                <h2 className="text-base font-semibold text-slate-900">
                  {t('backoffice.customers_detail_retained_email_title')}
                </h2>
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-slate-500">
                      {t('backoffice.customers_detail_delete_count')}
                    </dt>
                    <dd className="font-medium">{data.retainedEmail.deleteCount}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">
                      {t('backoffice.customers_detail_first_seen')}
                    </dt>
                    <dd>
                      {new Date(data.retainedEmail.firstSeenAt).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">
                      {t('backoffice.customers_detail_last_deleted')}
                    </dt>
                    <dd>
                      {data.retainedEmail.lastDeletedAt
                        ? new Date(
                            data.retainedEmail.lastDeletedAt,
                          ).toLocaleString()
                        : '—'}
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}

            {canOrderRead && data.liveOrders.length > 0 ? (
              <section>
                <h2 className="mb-3 text-base font-semibold text-slate-900">
                  {t('backoffice.customer_archive_detail_orders_live')}
                </h2>
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">
                          {t('backoffice.customer_archive_detail_order_id')}
                        </th>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">
                          {t('backoffice.customers_table_col_status')}
                        </th>
                        <th className="px-4 py-2 text-left font-semibold text-slate-700">
                          {t('backoffice.customer_archive_detail_batch')}
                        </th>
                        <th className="px-4 py-2 text-right font-semibold text-slate-700">
                          {t('backoffice.customer_archive_detail_open_order')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.liveOrders.map((o) => (
                        <tr key={o.id}>
                          <td className="px-4 py-2 font-mono text-xs">{formatOrderReference(o.id)}</td>
                          <td className="px-4 py-2">{o.status}</td>
                          <td className="max-w-[200px] px-4 py-2 text-xs text-slate-600">
                            {o.deliveryBatch
                              ? `${o.deliveryBatch.areaLabel} (${o.deliveryBatch.status})`
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Link
                              to={`${WORKSPACE_BASE}/orders?q=${encodeURIComponent(o.id)}&view=ALL`}
                              className="font-medium text-brand-600 hover:text-brand-700"
                            >
                              {t('backoffice.customer_archive_detail_open_order')}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <section>
              <h2 className="mb-2 text-base font-semibold text-slate-900">
                {t('backoffice.customer_archive_detail_snapshot_json')}
              </h2>
              <p className="mb-3 text-xs text-slate-500">
                {t('backoffice.customer_archive_detail_snapshot_hint')}
              </p>
              <pre className="max-h-[480px] overflow-auto rounded-lg border border-slate-200 bg-slate-950 p-4 text-xs text-emerald-100">
                {payloadJson}
              </pre>
            </section>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
